/**
 * PB-Profil — Options
 *
 * Two sources:
 *   1. `profile_options` table → dynamic, admin-editable. Loaded via API
 *      `v1ProfileOptions.grouped()` and passed in as `opts`.
 *   2. Hardcoded values for fields not in the options table (anrede, badge,
 *      impfstatus, krankenkasse_typ, locale, …) — sourced from CRM 1.0
 *      ProfileData.js where they live as static `Selectbox` props.
 *
 * The DB-fallback values mirror the seed in
 *   crm1.0/sh24-api/database/migrations/2020_11_02_234042_create_profile_options_table.php
 * so the UI works even before the API call completes.
 */
import type { V1ProfileOption } from '../../../lib/v1';
import type { SelectOption } from './controls';

/** DB-seed fallback (used until v1ProfileOptions.grouped() returns). */
export const FALLBACK_OPTS: Record<string, string[]> = {
  // Rollen im Familienkonto — Seed aus
  // 2026_07_06_100004_seed_profiltyp_familiengrad_options. Ohne diesen Eintrag
  // stünde jede Rollen-Auswahl leer da, solange/falls die Optionen nicht laden.
  // Befund 23.09.: "Frau"/"Mann" nicht mehr im Fallback-Angebot — sie werden
  // nur noch angezeigt, wenn ein bestehender Datensatz sie bereits trägt
  // (dann kommen sie über die DB-Liste `opts`, gefiltert per is_selectable/
  // currentValue in fieldOptions()/labelOptions() unten, nicht über diesen
  // Fallback, der ausschließlich vor dem ersten API-Rückgabewert greift).
  profiltyp: ['Rechnungsempfänger', 'Hausarzt', 'Kontaktperson'],
  familienstand: ['ledig', 'verheiratet', 'geschieden', 'verwitwet', 'Lebensgemeinschaft'],
  pflegegrad: ['Pflegegrad 1', 'Pflegegrad 2', 'Pflegegrad 3', 'Pflegegrad 4', 'Pflegegrad 5'],
  beweglichkeit: [
    'völlig selbstständig',
    'selbstständig am Rollator',
    'mit leichter Hilfe (kein Heben)',
    'mit deutlicher Hilfe, etwas Heben',
    'überwiegend im Rollstuhl (Umsetzen selbständig möglich, mit leichter Hilfe)',
    'sehr stark eingeschränkt (Umsetzen / Umlagern mit Körperlift)',
    'komplett bettlägerig',
  ],
  einschraenkungen: [
    'keine Einschränkungen, rüstiger Rentner/In',
    'schwacher Gesamtzustand / Altersschwäche',
    'Herz-Kreislauf-Erkrankung',
    'COPD / Atemnot',
    'Demenz / Alzheimer',
    'Parkinson',
    'Defekt an Knie- oder Hüftgelenken',
    'Einschränkung nach Knie- oder Hüft-OP',
    'Einschränkung nach Knochenbruch',
    'Osteoporose',
    'Gleichgewichtsprobleme / PNP-taube Füße',
    'Diabetes',
    'Aktuelle Krebserkrankung',
    'Einschränkung der oberen Extremitäten, z.B. Lähmung nach Schlaganfall',
    'Einschränkung der unteren Extremitäten, z.B. Lähmung nach Schlaganfall',
    'Schluckstörung',
    'Nervenleiden / Dauerhafte Schmerzen',
    'Starke Wassereinlagerung',
    'Offene Beine Ulcus cruris',
  ],
  besserung: ['Ja', 'Nein'],
  sinneswahrnehmung: [
    'keine Einschränkungen',
    'beim Hören eingeschränkt',
    'beim Sehen eingeschränkt',
    'beim Sprechen eingeschränkt',
  ],
  geisteszustand: ['normal', 'altersbedingt vergesslich', 'leicht dement', 'dement'],
  schlafverhalten: ['ohne Störung', '1-2x wach (z.B. Toilettengang)', 'stark gestört, länger wach'],
  essen: ['keine Hilfe', 'leichte Hilfe (Happen vorbereiten)', 'komplette Hilfe, Füttern'],
  grundpflege: [
    'selbstständig',
    'mit leichter Unterstützung',
    'mit deutlicher Unterstützung',
    'durch komplette Übernahme',
  ],
  hygiene: [
    'Senior/In schafft es selbstständig',
    'Senior/In benötigt Kontrolle / leichte Hilfe',
    'Senior/In ist inkontinent, komplette Übernahme',
  ],
  urinkontrolle: [
    'Senior/In schafft es selbstständig',
    'Senior/In kann noch ansagen, benötigt Einlage / Windel zur Vorsicht',
    'Senior/In ist inkontinent, benötigt Windel mit Wechsel',
  ],
  stuhlkontrolle: [
    'Senior/In schafft es selbstständig',
    'Senior/In kann noch ansagen, benötigt Einlage / Windel zur Vorsicht',
    'Senior/In ist inkontinent, benötigt Windel mit Wechsel',
  ],
  ankleiden: ['keine Hilfe', 'leichte Unterstützung', 'komplette Übernahme'],
  wohnsituation: [
    'Senior/In lebt in einer Wohnung in Einfamilienhaus',
    'Senior/In lebt in einem Haus (Reihenhaus, DHH, EFH)',
    'Senior/In lebt in einer Wohnung in einem Mehrparteienhaus',
    'Senior/In lebt in Bauernhaus mit aktiver Landwirtschaft',
  ],
  wohnzustand: [
    'eher einfach',
    'normal, typisch für eine Seniorenwohnung',
    'behindertengerecht',
    'modern',
    'insgesamt eher abgewohnt',
    'besonders sauber',
    'gehobene Ausstattung',
    'besonders vornehm und sehr gepflegt',
  ],
  wohnort: ['Großstadt', 'Kleinstadt', 'Dorf'],
  haustiere: ['Nein', 'Ja'],
  haustiere_welche: ['Hund', 'Katze', 'Sonstige Kleintiere'],
  einkaufsmoeglichkeiten: [
    'ca. 5 Minuten bis 500 Meter entfernt',
    'ca. 15 Minuten bis 1500 Meter entfernt',
    'ca. 30 Minuten bis 3000 Meter entfernt',
    'weiter weg',
  ],
  bk_wohnen: ['Zimmer', 'extra WC', 'eigenes Bad', 'eigene Wohnung'],
  charaktereigenschaften: [
    'freundlich, gastfreundlich',
    'gut zu führen',
    'dankbar',
    'besonders herzlich',
    'humorvoll',
    'besonders sensibel',
    'eigensinnig',
    'stur',
    'verschlossen, introvertiert',
    'sehr ruhig',
    'herablassend',
    'teilnahmslos',
    'bestimmend',
  ],
  raucher: ['Nein', 'Ja'],
  bk_aktivitaeten: [
    'Begleitung bei Spaziergängen',
    'Begleitung bei Arztbesuchen',
    'Begleitung zu Restaurant etc.',
    'Aktivieren und Motivieren',
  ],
  bk_anrede: ['weiblich', 'männlich'],
  bk_deutsch: [
    'Gute bis sehr gute Kenntnisse, großer Wortschatz, grammatikalische Fehler',
    'Ordentliche bis gute Kenntnisse, ausreichender Wortschatz, um sich gut verständigen zu können',
    'Grundkenntnisse mit geringem Wortschatz, Verständigung eingeschränkt möglich, Google-Übersetzter via Smartphone empfohlen',
  ],
  bk_raucher: ['Ja', 'Nein'],
  bk_fuehrerschein: ['Ja', 'Nein'],
};

/**
 * Befund 23.09.: eine Option mit `is_selectable === false` (z.B. profiltyp
 * "Frau"/"Mann", Migration 2026_09_23_100000) fällt aus dem Dropdown-Angebot
 * — AUSSER sie ist die aktuell gesetzte `currentValue` einer bestehenden
 * Auswahl. Ohne diese Ausnahme würde ein natives <select> für ein bereits
 * gespeichertes "Frau"/"Mann" plötzlich keine passende <option> mehr finden
 * und leer/verschoben anzeigen, obwohl der Wert weiterhin gilt.
 */
function isOfferable(o: V1ProfileOption, currentValue: string | null | undefined, valueOf: (o: V1ProfileOption) => string): boolean {
  return o.is_selectable !== false || valueOf(o) === currentValue;
}

/**
 * Build {value: id-as-string, label: text} pairs for a Select / CheckGroup.
 * If the DB-loaded `opts` lookup contains the field, use those (with real
 * primary-key ids — that's what the API expects). Otherwise fall back to the
 * seeded labels using the label itself as both value and label.
 *
 * `currentValue` (die ID als String, wie sie im Formularstate steht): eine
 * bereits gesetzte, nicht mehr wählbare Option bleibt trotzdem in der Liste
 * (s. `isOfferable`).
 */
export function fieldOptions(
  name: string,
  opts: Record<string, V1ProfileOption[]> | null,
  currentValue?: string | null,
): SelectOption[] {
  if (opts && opts[name]?.length) {
    return opts[name]
      .filter(o => isOfferable(o, currentValue, x => String(x.id)))
      .map(o => ({ value: String(o.id), label: o.value }));
  }
  const fallback = FALLBACK_OPTS[name] ?? [];
  return fallback.map(v => ({ value: v, label: v }));
}

/**
 * Wie `fieldOptions`, aber mit dem **Label** als `value` (statt der Options-ID).
 *
 * Nötig für Felder, deren Backend gegen `profile_options.value` (das Label)
 * validiert statt gegen die ID — im Familienkonto sind das `profiltyp` und
 * `familiengrad`. Das geteilte `fieldOptions` bleibt für alle anderen (ID-
 * basierten) Felder unverändert.
 *
 * `currentValue` hier ist das Label selbst (z.B. "Frau").
 */
export function labelOptions(
  name: string,
  opts: Record<string, V1ProfileOption[]> | null,
  currentValue?: string | null,
): SelectOption[] {
  if (opts && opts[name]?.length) {
    return opts[name]
      .filter(o => isOfferable(o, currentValue, x => x.value))
      .map(o => ({ value: o.value, label: o.value }));
  }
  const fallback = FALLBACK_OPTS[name] ?? [];
  return fallback.map(v => ({ value: v, label: v }));
}

/** Nationalität-Dropdown; ein gespeicherter Altwert außerhalb der Liste bleibt wählbar. */
export function nationalitaetOptions(currentValue?: string | null): string[] {
  const list = HARDCODED.nationalitaet;
  return currentValue && !list.includes(currentValue) ? [currentValue, ...list] : list;
}

/* ─── Hardcoded options (not stored in profile_options table) ─── */
export const HARDCODED = {
  anrede: ['Herr', 'Frau'],
  krankenkasse_typ: [
    { value: 'gesetzlich', label: 'Gesetzlich' },
    { value: 'privat', label: 'Privat' },
  ] as SelectOption[],
  badge: [
    { value: '0', label: 'Kein Status' },
    { value: '1', label: 'Klassik' },
    { value: '2', label: 'Klassik Plus' },
    { value: '3', label: 'Exzellent' },
  ] as SelectOption[],
  impfstatus: [
    { value: '1', label: 'Ungeimpft' },
    { value: '2', label: 'Geimpft' },
    { value: '3', label: 'Booster' },
  ] as SelectOption[],
  locale: [
    { value: 'de', label: 'Deutsch' },
    { value: 'en', label: 'Englisch' },
    { value: 'pl', label: 'Polnisch' },
  ] as SelectOption[],
  // ISO country codes — only the common ones; backend stores the code.
  land: [
    { value: 'DE', label: 'Deutschland' },
    { value: 'AT', label: 'Österreich' },
    { value: 'CH', label: 'Schweiz' },
    { value: 'FR', label: 'Frankreich' },
    { value: 'IT', label: 'Italien' },
    { value: 'PL', label: 'Polen' },
    { value: 'NL', label: 'Niederlande' },
    { value: 'BE', label: 'Belgien' },
    { value: 'LU', label: 'Luxemburg' },
  ] as SelectOption[],
  // profiles.nationalitaet ist ein freier String (Altbestand z. B. „Deutsch").
  // Gespeichert wird das Label selbst; ein abweichender Altwert wird in der
  // Maske zusätzlich angeboten (s. nationalitaetOptions()).
  nationalitaet: [
    'Deutsch', 'Österreichisch', 'Schweizerisch', 'Polnisch', 'Rumänisch',
    'Bulgarisch', 'Ungarisch', 'Tschechisch', 'Slowakisch', 'Kroatisch',
    'Slowenisch', 'Litauisch', 'Lettisch', 'Estnisch', 'Italienisch',
    'Französisch', 'Niederländisch', 'Belgisch', 'Luxemburgisch', 'Spanisch',
    'Portugiesisch', 'Griechisch', 'Türkisch', 'Serbisch', 'Bosnisch',
    'Ukrainisch', 'Russisch', 'Sonstige',
  ],
  /**
   * prices_pb.vertrag — PB-Vertragsarten mit den Schlüsseln aus CRM 1.0
   * (`pbContracts` in scripts/Functions.js). Die Schlüssel filtern Liste und
   * Masterliste (PbListController, Care_Export_Sheet_Cares) und dürfen sich
   * deshalb nicht ändern.
   */
  vertrag: [
    { value: 'dpfv', label: 'Deutscher Pflegedienstvertrag' },
    { value: 'pv',   label: 'Polnischer Dienstleistungsvertrag' },
    { value: 'd+pv', label: 'Kombi-Vertrag (Deutsch & Polnisch)' },
  ] as SelectOption[],
  // Status options for users.status (header summary card)
  status: [
    { value: '1', label: 'Aktiv' },
    { value: '2', label: 'Ausstehend' },
    { value: '3', label: 'Pausiert' },
    { value: '0', label: 'Beendet' },
    { value: '9', label: 'Verstorben' },
  ] as SelectOption[],
  konto: [
    { value: '1', label: 'Interessent' },
    { value: '2', label: 'Aktiver Kunde' },
    { value: '3', label: 'Pausiert' },
    { value: '4', label: 'Ehemaliger Kunde' },
    { value: '0', label: 'Gesperrt' },
  ] as SelectOption[],
};
